import { Dtos, SuretyAccountId, WorkInProgressStatementId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { wipQueryKey } from "../../account/account.keys";
import { updateWipStatementUnderwriterLock } from "./utils";

export const useAdminLockWipStatement = (accountId: SuretyAccountId) => {
	const queryClient = useQueryClient();
	const { post } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationKey: ["lockWipStatement"],
		mutationFn: async (wipStatementId: WorkInProgressStatementId) => {
			await post(`/v2/admin/surety/wip-statement/${wipStatementId}/lock`);
		},
		onMutate: async (wipStatementId: WorkInProgressStatementId) => {
			await queryClient.cancelQueries({ queryKey: wipQueryKey(accountId) });
			const previousWipStatements = queryClient.getQueryData<
				Dtos.Admin.WipStatement.Get.Response[]
			>(wipQueryKey(accountId));
			queryClient.setQueryData(
				wipQueryKey(accountId),
				(old: Dtos.Admin.WipStatement.Get.Response[] | undefined) => {
					if (!old) throw new Error("No WIP statement data found");
					return updateWipStatementUnderwriterLock(true, old, wipStatementId);
				},
			);
			return { previousWipStatements };
		},
		onError: (error: Error, _data, context) => {
			queryClient.setQueriesData(
				{ queryKey: wipQueryKey(accountId) },
				context?.previousWipStatements,
			);
			logError(error);
			triggerErrorModal(error);
		},
	});

	return {
		lockWipStatement: mutation.mutate,
		lockWipStatementIsLoading: mutation.isPending,
		lockWipStatementSuccess: mutation.isSuccess,
	};
};
